// ===========================================
// Operational Efficiency Analytics Page
// ===========================================
// Comprehensive operational metrics including room utilization, teacher workload, and conversion funnels

import { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Assessment as AssessmentIcon } from '@mui/icons-material';

import { OperationsFilters as OperationsFiltersType } from '../../../api/reports.api';
import {
  useRoomUtilization,
  usePeakHours,
  useTeacherWorkload,
  useExtraLessonMetrics,
  useMeetGreetConversion,
  useEnquiryFunnel,
} from '../../../hooks/useReports';
import PageHeader from '../../../components/common/PageHeader';
import OperationsFilters from '../../../components/reports/OperationsFilters';
import RoomUtilizationChart from '../../../components/reports/RoomUtilizationChart';
import PeakHourHeatmap from '../../../components/reports/PeakHourHeatmap';
import TeacherWorkloadTable from '../../../components/reports/TeacherWorkloadTable';
import ExtraLessonMetricsCards from '../../../components/reports/ExtraLessonMetricsCards';
import MeetGreetFunnelChart from '../../../components/reports/MeetGreetFunnelChart';
import EnquiryFunnelChart from '../../../components/reports/EnquiryFunnelChart';

// ===========================================
// COMPONENT
// ===========================================

export default function OperationalEfficiencyPage() {
  // Filter state
  const [filters, setFilters] = useState<OperationsFiltersType>({});
  const [appliedFilters, setAppliedFilters] = useState<OperationsFiltersType>({});
  const [conversionMonths, setConversionMonths] = useState<number>(12);
  const [enquiryFunnelMonths, setEnquiryFunnelMonths] = useState<number>(12);

  // Fetch data with applied filters
  const { data: roomUtilization, isLoading: roomLoading } = useRoomUtilization(appliedFilters);
  const { data: peakHours, isLoading: peakLoading } = usePeakHours(appliedFilters);
  const { data: teacherWorkload, isLoading: workloadLoading } = useTeacherWorkload(appliedFilters);
  const { data: extraLessonMetrics, isLoading: metricsLoading } = useExtraLessonMetrics(appliedFilters);
  const { data: meetGreetConversion, isLoading: conversionLoading } = useMeetGreetConversion(conversionMonths);
  const { data: enquiryFunnel, isLoading: enquiryFunnelLoading, isError: enquiryFunnelError } = useEnquiryFunnel(enquiryFunnelMonths);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleResetFilters = () => {
    setFilters({});
    setAppliedFilters({});
  };

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Operational Efficiency Analytics"
        subtitle="Room utilization, teacher workload, and operational metrics"
        icon={<AssessmentIcon />}
      />

      {/* Filters */}
      <OperationsFilters
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* Extra Lesson Metrics Cards */}
      <ExtraLessonMetricsCards data={extraLessonMetrics} loading={metricsLoading} />

      {/* Charts Grid Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Room Utilization Chart - 6 columns */}
        <Grid item xs={12} md={6}>
          <RoomUtilizationChart data={roomUtilization || []} loading={roomLoading} />
        </Grid>

        {/* Peak Hour Heatmap - 6 columns */}
        <Grid item xs={12} md={6}>
          <PeakHourHeatmap data={peakHours || []} loading={peakLoading} />
        </Grid>
      </Grid>

      {/* Teacher Workload Table - Full Width */}
      <Box sx={{ mb: 3 }}>
        <TeacherWorkloadTable data={teacherWorkload || []} loading={workloadLoading} />
      </Box>

      {/* Meet & Greet Conversion Funnel - Full Width with Month Selector */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Time Range
            </Typography>
            <ToggleButtonGroup
              value={conversionMonths}
              exclusive
              onChange={(_, value) => value && setConversionMonths(value)}
              size="small"
            >
              <ToggleButton value={6}>6 Months</ToggleButton>
              <ToggleButton value={12}>12 Months</ToggleButton>
              <ToggleButton value={24}>24 Months</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </CardContent>
      </Card>
      <Box sx={{ mt: 2 }}>
        <MeetGreetFunnelChart data={meetGreetConversion || []} loading={conversionLoading} />
      </Box>

      {/* Enquiry Conversion Funnel - Full Width with Month Selector */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Enquiry Funnel Time Range
            </Typography>
            <ToggleButtonGroup
              value={enquiryFunnelMonths}
              exclusive
              onChange={(_, value) => value && setEnquiryFunnelMonths(value)}
              size="small"
            >
              <ToggleButton value={6}>6 Months</ToggleButton>
              <ToggleButton value={12}>12 Months</ToggleButton>
              <ToggleButton value={24}>24 Months</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </CardContent>
      </Card>
      <Box sx={{ mt: 2 }}>
        <EnquiryFunnelChart data={enquiryFunnel} loading={enquiryFunnelLoading} isError={enquiryFunnelError} />
      </Box>
    </Box>
  );
}
